import { autoinject } from "aurelia-framework";

import { FetchClient } from "../common/fetch-client";
import config from "../common/config";
import {
    ICreateAsset,
    ICreateAssetForMessagingResult,
    ICreateAssetItem,
    IFile,
    IFileDetails,
    IGetAssetResponse
} from "../resources-vue/vue-interfaces/i-asset";
import {
    IGetConversationQuery,
    IGetConversationResult,
    IGetMessageQuery,
    IGetMessageResult,
    IMessageItem,
    IGetConversationChunkQuery,
    INewMessage,
    ISendMessageQuery,
    ISendMessageResult,
    IGetSharedAssetQuery,
    IGetSharedAssetResult,
    IDeleteMessages,
    ICreateRoomRequest,
    ICreateRoomResponse,
    IJoinRoomResponse,
    IGetRoomStatusResponse,
    IConversationRoomStatusResult,
    IStartConversationRoomRequest,
    IStartConversationRoomResponse,
    ISendCallNotificationRequest,
    IMarkCallAttendanceForUserRequest,
    IEditMessageQuery,
    IGetMessageByIdResult,
    IAddMembers,
    IAssetDetails,
    ISucessResponse,
    IGetConversationByIdResult,
    IConversationUser,
    ISuccessUserResponse,
    IGetAxxessConnectPermissionResponse,
    IGetUnreadConversationQuery
} from "../resources-vue/vue-interfaces/messaging-service/i-message";
import { ISuccessResponse } from "../resources-vue/vue-interfaces/i-common";
import {
    ICallHistoryRequest,
    ICallHistoryResponse
} from "../resources-vue/vue-interfaces/messaging-service/i-call-history";
import {
    IGetUsersByNameQuery,
    IGetUserStatusResult,
    ISetUserProfile,
    IUsersList,
    IUsersProfileList
} from "../resources-vue/vue-interfaces/messaging-service/i-user";
import {
    IGetPatientsQuery,
    IPatientListResponse,
    IPatientList
} from "../resources-vue/vue-interfaces/messaging-service/i-patient";
import { IPaginationCamelCaseNew } from "./user-service";

@autoinject()
export class MessageService {
    private _httpClient: FetchClient;
    private _baseUrl: string = `/api/v1/conversations`;
    private _baseMessagingUrl: string = `${config.messagingServiceServerURL}/api/v1`;
    private queryHeaders = {
        "X-Chat-AgencyId": "122ec20a-705b-4974-9437-8f6233a6e953"
    };

    public constructor(httpClient: FetchClient) {
        this._httpClient = httpClient;
    }

    public async getConversations(query: IGetConversationQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations`)
            .useMode("cors")
            .useQueryString(query)
            .useHeaders(this.queryHeaders)
            .fetch<IGetConversationResult>();
    }

    public async getConversationById(id: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations/${id}`)
            .useMode("cors")
            .useHeaders(this.queryHeaders)
            .fetch<IGetConversationByIdResult>();
    }

    public async getUnreadConversations(query: IGetUnreadConversationQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations/unread`)
            .useQueryString(query)
            .useHeaders(this.queryHeaders)
            .fetch<IGetConversationResult>();
    }

    public async getMessages(conversationId: string, query: IGetMessageQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations/${conversationId}/messages`)
            .useQueryString(query)
            .useHeaders(this.queryHeaders)
            .fetch<IGetMessageResult>();
    }

    public async getMessageById(conversationId: string, messageId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations/${conversationId}/messages/${messageId}`)
            .useHeaders(this.queryHeaders)
            .fetch<IGetMessageByIdResult>();
    }

    public async getNewerMessages(conversationId: string, query: IGetMessageQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations/${conversationId}/messages/newer`)
            .useQueryString(query)
            .useHeaders(this.queryHeaders)
            .fetch<IGetMessageResult>();
    }

    public async getConversationRoomStatus(conversationId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/room`)
            .useHeaders(this.queryHeaders)
            .fetch<IConversationRoomStatusResult>();
    }

    public async sendCallNotification(roomId: string, payload: ISendCallNotificationRequest) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/vcc/room/${roomId}/send-call-notification`)
            .useHeaders(this.queryHeaders)
            .useMethod("POST")
            .useBody(payload)
            .fetch();
    }

    public async markCallAttendanceForUser(conversationId: string, payload: IMarkCallAttendanceForUserRequest) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/room-attendance`)
            .useHeaders(this.queryHeaders)
            .useMethod("POST")
            .useBody(payload)
            .fetch();
    }

    public async startConversationRoom(conversationInfo: IStartConversationRoomRequest) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/start-room`)
            .useHeaders(this.queryHeaders)
            .useMethod("POST")
            .useBody(conversationInfo)
            .fetch<IStartConversationRoomResponse>();
    }

    public async getAssets(
        conversationId: string,
        messageId: string,
        assetIds: string[]
    ): Promise<IGetAssetResponse[][]> {
        const assetPromises = assetIds.map(async (assetId) => {
            return await this._httpClient
                .build(`${this._baseMessagingUrl}/messages/${conversationId}/${messageId}/${assetId}`)
                .fetch<IGetAssetResponse[]>();
        });

        return Promise.all(assetPromises);
    }

    public async getUnReadMessages(conversationId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations/${conversationId}/unread`)
            .fetch<IMessageItem[]>();
    }

    public async getConversationChunk(conversationId: string, query: IGetConversationChunkQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations/${conversationId}/chunk`)
            .useQueryString(query)
            .fetch<IMessageItem[]>();
    }

    public async newConversation(newMessage: INewMessage) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations`)
            .useHeaders(this.queryHeaders)
            .useMethod("POST")
            .useBody(newMessage)
            .fetch<any>();
    }

    public async markConversationAsRead(conversation: { conversationId: string }) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/read-message`)
            .useHeaders(this.queryHeaders)
            .useMethod("PATCH")
            .useBody(conversation)
            .fetch();
    }

    public async addNewMemberToConv(conversationId: string, query: IAddMembers) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/add-participants`)
            .useMethod("PATCH")
            .useHeaders(this.queryHeaders)
            .useBody(query)
            .fetch<ISuccessResponse>();
    }

    public async leaveGroup(conversationId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/leave`)
            .useHeaders(this.queryHeaders)
            .fetch<ISucessResponse>();
    }

    public async editGroupName(conversationId: string, newTitle: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/edit-title`)
            .useMethod("POST")
            .useBody({ newTitle })
            .useHeaders(this.queryHeaders)
            .fetch<ISucessResponse>();
    }

    public async removeMember(conversationId: string, userId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/remove-participant/${userId}`)
            .useHeaders(this.queryHeaders)
            .fetch<ISucessResponse>();
    }

    public async sendMessage(conversationId: string, query: ISendMessageQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations/${conversationId}/message`)
            .useMethod("PUT")
            .useHeaders(this.queryHeaders)
            .useBody(query)
            .fetch<ISendMessageResult>();
    }

    public async editMessage(conversationId: string, messageId: string, query: IEditMessageQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/${messageId}`)
            .useMethod("PUT")
            .useHeaders(this.queryHeaders)
            .useBody(query)
            .fetchNoContent();
    }

    public async deleteMessage(conversationId: string, messageId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/${messageId}`)
            .useMethod("DELETE")
            .useHeaders(this.queryHeaders)
            .fetchNoContent();
    }

    public async undoDeletedMessage(conversationId: string, messageId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/${messageId}/undo-delete`)
            .useMethod("PATCH")
            .useHeaders(this.queryHeaders)
            .fetch<ISuccessResponse>();
    }

    public async getSharedAssets(conversationId: string, query: IGetSharedAssetQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations/${conversationId}/assets`)
            .useQueryString(query)
            .fetch<IGetSharedAssetResult[]>();
    }

    public async deleteMessages(conversationId: string, data: IDeleteMessages) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations/${conversationId}/messages`)
            .useMethod("DELETE")
            .useBody(data)
            .fetchNoContent();
    }

    public async getDeletedMessages(conversationId: string, query: IGetMessageQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations/${conversationId}/deleted-messages`)
            .useQueryString(query)
            .fetch<IGetMessageResult>();
    }

    public async getUserByName(query: IGetUsersByNameQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/users`)
            .useMethod("POST")
            .useBody(query)
            .useHeaders(this.queryHeaders)
            .fetch<IPaginationCamelCaseNew<IUsersList>>();
    }

    public async getUsersForProfile(query: IGetUsersByNameQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/users/filter`)
            .useMethod("POST")
            .useBody(query)
            .useHeaders(this.queryHeaders)
            .fetch<IPaginationCamelCaseNew<IUsersProfileList>>();
    }

    public async getPatientsByName(query: IGetPatientsQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/patients`)
            .useMethod("POST")
            .useBody(query)
            .useHeaders(this.queryHeaders)
            .fetch<IPatientListResponse<IPatientList>>();
    }

    public async getActivePatientsByName(query: IGetPatientsQuery) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/patients/active`)
            .useMethod("POST")
            .useBody(query)
            .useHeaders(this.queryHeaders)
            .fetch<IPatientListResponse<IPatientList>>();
    }

    public async uploadMessageFiles(files: File[]) {
        if (!files?.length) {
            return [];
        }
        const fileDetails: IFile[] = files.map((file, index) => ({
            fileName: file.name,
            fileSize: file.size,
            attachmentNumber: index
        }));
        const payload: ICreateAsset = {
            assets: [...fileDetails],
            page: 0,
            pageLength: 0
        };
        const assets: ICreateAssetForMessagingResult = await this.createMessageAssets(payload);
        if (!assets?.assetDetails?.length) {
            throw new Error("Asset Creation Unsuccessful.");
        }
        let successfulAssets: IAssetDetails[] = [];
        assets.assetDetails?.forEach((asset) => {
            if (asset.isSuccessful) {
                let file = files[asset.attachmentNumber];
                this.upload(asset.token, file);
                successfulAssets.push({ assetId: asset.assetId, contentType: files[asset.attachmentNumber].type });
            }
        });
        return successfulAssets;
    }

    private async createMessageAssets(fileDetails: ICreateAsset) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/assets`)
            .useMethod("POST")
            .useBody(fileDetails)
            .useHeaders(this.queryHeaders)
            .fetch<ICreateAssetForMessagingResult>();
    }

    public async getAssetFromId(query: any) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/assets`)
            .useQueryString(query)
            .useHeaders(this.queryHeaders)
            .fetch<any>();
    }

    public async upload(token: string, file: File) {
        let formData = new FormData();
        formData.append("File", file);
        return await this._httpClient
            .build(`${config.assetServerStorageURL}/api/v1/assets`)
            .useMode("cors")
            .useMethod("POST")
            .useAuthorization("Bearer " + token)
            .useFormData(formData)
            .fetchNoContent();
    }

    public async createRoom(query: ICreateRoomRequest) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/vcc/create/room`)
            .useMethod("POST")
            .useBody(query)
            .useHeaders(this.queryHeaders)
            .fetch<ICreateRoomResponse>();
    }

    public async joinRoom(roomId: string) {
        return await this._httpClient
            .build(`${config.AudioVideoServiceServerURL}/api/v1/User/join-room/${roomId}`)
            .fetch<IJoinRoomResponse>();
    }

    public async declineCall(conversationId: string, roomId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/decline-call`)
            .useMethod("POST")
            .useBody({ roomId })
            .useHeaders(this.queryHeaders)
            .fetch<ISucessResponse>();
    }

    public async acceptCall(conversationId: string, roomId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/accept-call`)
            .useMethod("POST")
            .useBody({ roomId })
            .useHeaders(this.queryHeaders)
            .fetch<ISucessResponse>();
    }

    public async unansweredCall(conversationId: string, roomId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/vcc/room/${roomId}/log-unanswered-call`)
            .useMethod("POST")
            .useBody({ conversationId })
            .useHeaders(this.queryHeaders)
            .fetch<ISucessResponse>();
    }

    public async getRoomStatus(roomId: string, subjectId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/vcc/room/${roomId}/${subjectId}`)
            .fetch<IGetRoomStatusResponse>();
    }

    // Call endpoints
    public async getCallInfo(query: ICallHistoryRequest) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/calls`)
            .useQueryString(query)
            .useHeaders(this.queryHeaders)
            .fetch<ICallHistoryResponse>();
    }

    public async deleteConversation(conversationId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}`)
            .useMethod("DELETE")
            .fetch<ISuccessResponse>();
    }

    public async readAllMessages() {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/read-all-messages`)
            .useHeaders(this.queryHeaders)
            .fetch<ISuccessResponse>();
    }

    public async getAxxessConnectToggle() {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/permission/get-axxess-connect-toggle`)
            .useHeaders(this.queryHeaders)
            .fetch<IGetAxxessConnectPermissionResponse>();
    }

    public async toggleMute(mute: boolean, conversationId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/${mute ? "mute" : "un-mute"}`)
            .useMethod("PATCH")
            .fetch<ISuccessResponse>();
    }

    public async getConversationUsers(conversationId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/conversations/${conversationId}/users`)
            .fetch<ISuccessUserResponse<IPaginationCamelCaseNew<IConversationUser>>>();
    }

    public async sendReaction(messageId: string, conversationId: string, emoji: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/${messageId}/reaction`)
            .useMethod("POST")
            .useBody({ emoji })
            .fetch<ISuccessResponse>();
    }

    public async deleteReaction(messageId: string, conversationId: string) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/messages/${conversationId}/${messageId}/reaction`)
            .useMethod("DELETE")
            .fetchNoContent();
    }

    // ----------------------
    // user status endpoints
    // ----------------------

    public async getCurrUserProfileDetails() {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/users/connect/status`)
            .useMode("cors")
            .useHeaders(this.queryHeaders)
            .fetch<IGetUserStatusResult>();
    }

    public async updateUserProfile(payload: ISetUserProfile) {
        return await this._httpClient
            .build(`${this._baseMessagingUrl}/users/status/update`)
            .useMethod("PUT")
            .useHeaders(this.queryHeaders)
            .useBody(payload)
            .fetch<ISuccessResponse>();
    }
}
